/**
 * TO Appointments — `/appointments/technical` (module 01).
 *
 * The screen is scoped by **day** because the work is (§A1): every day the TD prepares and
 * approves all technical appointments for the next day's matches, across both competitions.
 * The competition tabs narrow that day and never widen it, exactly as the route does.
 *
 * Three fetches, deliberately independent:
 *  · the schedule, read only to learn which days have matches — a day picker built from
 *    real fixtures rather than a calendar of guesses;
 *  · the roster, which the slot picker needs. If it fails the day still renders, and the
 *    screen says plainly that no official can be chosen until it loads;
 *  · the day's panel itself, which fails closed into the shared banner.
 *
 * **The day is the whole of the scope this page fetches, and the competition is not.** It
 * used to be: choosing Men's re-fetched the day narrowed to one competition. That is the
 * wrong side of the wire for this screen. §A5's four panels each carry their own competition
 * tabs and each must be able to sit in a different one (Part C invariant 2), and every table
 * below the fixture list is derived live from the same client-side day — so a competition tab
 * that went to the server would empty three panels to answer one, and lose the change flash
 * on the way. The route still takes `competition` and the API still honours it; this screen
 * simply asks for the day and lets each panel narrow it. The scope can never widen either
 * way, which is the property §A1 is protecting.
 */

import { useState } from "react";

import {
  fetchAppointments,
  fetchAppointmentsSchedule,
  fetchFixtureDays,
  fetchOfficials,
} from "../api/client.js";
import type { AppointmentsViewBody, FixtureListBody, OfficialView } from "../api/types.js";
import { HowToLink, HowToUse } from "../app/HowToUse.js";
import { useTournament } from "../app/session.js";
import { EmptyState, FailBanner, Skeleton } from "../app/states.js";
import { useAsync } from "../app/useAsync.js";
import { AppointmentsPanel } from "../features/appointments/AppointmentsPanel.js";
import { stageDraftLabel } from "../features/appointments/derive.js";
import { landingDay, tournamentToday } from "../features/schedule/matchDays.js";

/** Every distinct match day in the schedule, in order. */
export function matchDays(body: FixtureListBody): string[] {
  return [...new Set(body.fixtures.map((fixture) => fixture.date))].sort();
}

export function AppointmentsPage(): React.JSX.Element {
  const [chosenDay, setChosenDay] = useState<string | null>(null);
  const tournament = useTournament();

  const schedule = useAsync(() => fetchFixtureDays(), []);
  const roster = useAsync(() => fetchOfficials(), []);

  const days = schedule.status === "ready" ? matchDays(schedule.data) : [];
  // Named from the schedule rather than assumed: when no fixture row carries a pool stage the
  // data cannot tell a stage from a knockout, and the button says the wider, honest thing.
  const stageLabel = stageDraftLabel(
    schedule.status === "ready" ? schedule.data.fixtures : [],
  );
  /**
   * RUN 82P (final QA, M3) — the day the page OPENS on is today, or the next match day after it,
   * and only then the first day of the schedule.
   *
   * It was `days[0]`, the first day of the whole tournament, while the Fixture List below it
   * landed on today (`landingDay`). The two disagreed from the second match day on: the list
   * showed today and the dropdown — which scopes `Draft Appointments`, `Confirm Day`,
   * `Mark Day Locked` and `Clear Drafts` — still named a day already settled. The TD chooses
   * freely from the dropdown; this is only where it starts.
   */
  const day =
    chosenDay ?? landingDay(days, tournamentToday(tournament?.timezone ?? null)) ?? days[0] ?? "";
  // The competitions this tournament actually runs, from the WHOLE schedule. The two bulk
  // controls cross every day, so the day on screen cannot tell them what exists.
  const competitions = [
    ...new Set(
      (schedule.status === "ready" ? schedule.data.fixtures : []).map(
        (fixture) => fixture.competitionId,
      ),
    ),
  ].sort();

  const panel = useAsync<AppointmentsViewBody | null>(
    () => (day === "" ? Promise.resolve(null) : fetchAppointments({ day })),
    [day],
  );

  /**
   * RUN 37 — the whole schedule's appointments, read once, for the Fixture List to list.
   *
   * A second read of the same route with no `day` on it, and deliberately a separate fetch
   * rather than a widening of the one above: the day fetch re-runs every time the TD changes
   * the Match Day dropdown and is the authority on the day he is appointing, while this one is
   * a backdrop that the day is drawn on top of. Merging them into a single request would make
   * every dropdown change re-read a fortnight to answer a question about one afternoon.
   *
   * If it fails the page is unharmed — the list falls back to the day, which is exactly what
   * it showed before this run — so it deliberately raises no banner of its own.
   *
   * **RUN 39 — read again when the Match Day changes.** Every row in the list is editable now,
   * on every day, so this backdrop is no longer a thing that can only have been changed by the
   * day panel sitting on top of it: an appointment made against Sunday while the dropdown said
   * Friday is a change to the backdrop itself. The panel is keyed on the day and remounts when
   * the dropdown moves, seeding its list from whatever this holds — so read once and the TD
   * would be shown Sunday exactly as it was before he edited it.
   *
   * One extra request per dropdown change, and the panel's own post-edit `refreshSchedule()`
   * keeps it current in between. Still a separate fetch from the day's, for the reason above:
   * the day read is the authority on what is being appointed and must not wait on a fortnight.
   */
  const schedulePanel = useAsync<AppointmentsViewBody | null>(
    // Held back until there is a day, exactly as the day read above is: the page's first paint
    // has no day at all while the fixture list is still loading, and a backdrop fetched then
    // would be a second request answering a question nothing has asked yet.
    () => (day === "" ? Promise.resolve(null) : fetchAppointmentsSchedule()),
    [day],
  );

  const officials: readonly OfficialView[] =
    roster.status === "ready" ? roster.data.officials : [];

  return (
    <>
      <div className="repband">
        <h2>Technical Appointments</h2>
        <HowToUse id="appointments" title="How to use Technical Appointments">
          <li>
            The <HowToLink to="fixture-list">Fixture List</HowToLink> holds the{" "}
            <b>whole schedule</b>, day by day, and opens on today&rsquo;s matches. Scroll up for
            the days behind you and down for the days ahead; each day carries its own heading
            row.
          </li>
          <li>
            Choose the day you are <b>appointing</b> from the <b>Match Day</b> dropdown in this
            heading band. That day is marked <b>Appointing</b> in the list, and it is the day
            the <b>Day Warnings</b> counts, the slots-filled line and every one of the buttons
            below mean. <b>You can change a slot on any day in the list</b>, not only that one
            &mdash; an appointment made on another day is saved against that day and leaves the
            dropdown, the counts and <b>Confirm Day</b> exactly where they were.
          </li>
          <li>
            Read the <HowToLink to="day-warnings">Day Warnings</HowToLink> counts before you
            press anything. On an empty day every slot is counted as needing attention. The
            <b> advisory</b> count also carries the tournament-wide notes &mdash; including any
            pair of officials sharing more matches than the tolerance set in Configuration
            §06. Those are worth knowing about and block nothing.
          </li>
          <li>
            Press <HowToLink to="draft-appointments">Draft Appointments</HowToLink>. The engine
            fills every open slot against the tournament&rsquo;s own rules, and the three
            derived tables fill with it.
          </li>
          <li>
            To draft the <b>whole first round in one press</b>, use{" "}
            <HowToLink to="draft-stage">{stageLabel}</HowToLink>. It runs one engine pass per
            day, in date order, with each day drafted against the days before it — so the
            workload, the team coverage and the colleague pairings even out across the whole
            stage instead of day by day. It asks first, then leaves a per-day strip showing
            what each day came to. Days you have already confirmed or locked, and any slot you
            have edited by hand, are left exactly as they are.
          </li>
          <li>
            If the draft is legal but you do not like how it has fallen &mdash; the same two
            people together four times, say &mdash; press{" "}
            <HowToLink to="redraft-stage">Re-Draft</HowToLink>. It runs the same stage draft
            with the engine&rsquo;s tie-break rotation moved on, so you get a{" "}
            <b>different arrangement, not a better one</b>: equally even, equally within the
            rules, paired and spread another way. Everything you have confirmed, edited by
            hand, pushed or overridden is kept. Press it again for another variant, and stop on
            the one you want &mdash; there is no way back to a variant you have drafted over.
          </li>
          <li>
            To change one appointment, click the official&rsquo;s <b>name</b> in the{" "}
            <HowToLink to="fixture-list">Fixture List</HowToLink>. The officials popover opens;
            anybody already in that match is greyed out and cannot be chosen.
          </li>
          <li>
            Choosing a struck-through name takes a second, deliberate press —{" "}
            <b>Confirm Override</b> — and records your name against it. The slot then carries a{" "}
            <b>Rule Breach</b> marker and the rule breach count goes up.
          </li>
          <li>
            Watch the counts move as you change a name. On a wide screen{" "}
            <HowToLink to="tech-table-panel">Tech Table Appointments</HowToLink> sits beside
            the Fixture List and stays there while you work, so the per-official totals answer
            an edit without you leaving the row. The{" "}
            <HowToLink to="matrix-panel">Appointment Matrix Table</HowToLink> and{" "}
            <HowToLink to="teams-coverage-panel">Teams Coverage</HowToLink> are below the
            list, each across the full width of the page; scroll down to them, or press their
            names here to be taken straight there. All three are counted from the appointments
            in front of you, so the cells whose numbers move flash as you work.
          </li>
          <li>
            <HowToLink to="clear-drafts">Clear Drafts</HowToLink> puts a day, or the whole
            stage, back to blank so you can rehearse the draft again. It asks which, and it is
            explicit about what goes: every unconfirmed appointment, <b>including ones you
            adjusted by hand</b>. Confirmed days, slots already pushed to TMS, recorded
            overrides and any match TMS has marked Official are never touched, and the result
            strip says how many were left alone.
          </li>
          <li>
            Press <HowToLink to="confirm-day">Confirm Day</HowToLink> to sign the day off. Each
            fixture then carries a <b>Confirmed</b> chip with the time it was signed off and a{" "}
            <b>Signed Off</b> chip saying it can no longer be changed. A signed-off slot can no
            longer be edited, and a match TMS has marked Official cannot be changed at all and
            says <b>Official in TMS</b>. Such a match is left as it is when you confirm the day,
            and the notice names it.
          </li>
          <li>
            To send a confirmed day to TMS, press{" "}
            <HowToLink to="push-day">Push Day To TMS</HowToLink>: every competition of the day,
            Men&rsquo;s then Women&rsquo;s. TMS saves each competition&rsquo;s day in one post, so
            your one Confirm in the extension&rsquo;s window makes one save per competition, one
            after the other, and publishes nothing. To push one competition on its own &mdash; a day
            where only one is ready, or one to push again &mdash; press{" "}
            <HowToLink to="push-tms">Push Men&rsquo;s To TMS</HowToLink> (or Women&rsquo;s).
            Either shows what it will push and asks first; then the <b>TMS Push extension</b> in
            this browser opens its own window, and nothing reaches TMS until you confirm it there. The day is
            held while the push is out, for ten minutes at most, and the answer says match by
            match what TMS accepted. A match it accepted is locked here as <b>On TMS</b>, unless
            its crew changed while the push was out or the result came after the hold ran out
            &mdash; the answer names each such match. If the extension does not answer, the button
            says what to check, and can be pressed again once it is fixed.
          </li>
          <li>
            Once you have entered the day&rsquo;s TO, SJ and TJ in TMS <b>and published them
            there</b>, press <HowToLink to="lock-day">Mark Day Locked</HowToLink>. It asks
            first, then marks every match on the day <b>On TMS</b>: those appointments become
            read-only here and count as the record of who officiated. This is how a day entered
            in TMS by hand is recorded; a pushed match is locked by the push. Every slot must be
            confirmed first &mdash; if one is not, nothing is locked and the message names the
            match. The one exception is a match TMS has already marked Official without it being
            confirmed here: it is left as it is, the question and the notice name it, and the
            button reads <b>Day Locked</b> once nothing else is left to lock &mdash; or{" "}
            <b>Nothing To Lock</b>, when every match on the day is such a match and none is
            locked.
          </li>
          <li>
            To correct a locked match, press <b>Unlock</b> on its row. It asks first, then makes
            that match&rsquo;s TO, SJ and TJ editable again, clears their confirmation and marks
            the match <b>Needs Re-Push</b> until the day is locked again. This screen does not
            change TMS: make the change here, correct the crew in TMS, then press{" "}
            <HowToLink to="confirm-day">Confirm Day</HowToLink> and{" "}
            <HowToLink to="lock-day">Mark Day Locked</HowToLink> again &mdash; with that
            match&rsquo;s day chosen in <b>Match Day</b>, if it is not the day you are appointing.
            A reopened slot offers no <b>Withdraw</b>: correct it by choosing the correct official
            from its name. Correct the crew and confirm it before TMS marks the match Official:
            once it is Official unconfirmed, it can no longer be confirmed or locked here, and an
            Official match cannot be unlocked.
          </li>
        </HowToUse>
        <span style={{ flex: 1 }} />
        <label className="daypick">
          <span className="kicker">Match Day</span>
          <select
            aria-label="Match day"
            value={day}
            disabled={days.length === 0}
            onChange={(event) => {
              setChosenDay(event.target.value);
            }}
          >
            {days.length === 0 ? <option value="">No match days loaded</option> : null}
            {days.map((entry) => (
              <option key={entry} value={entry}>
                {entry}
              </option>
            ))}
          </select>
        </label>
      </div>

      {schedule.status === "error" ? <FailBanner error={schedule.error} /> : null}
      {roster.status === "error" ? (
        <div className="warnnote warnnote--attention" role="status">
          <span className="warnnote__kind">Roster Not Loaded</span>
          <span className="warnnote__msg">
            The day below is shown as held, but no official can be chosen until the roster
            loads: {roster.error.message}
          </span>
        </div>
      ) : null}

      {panel.status === "loading" ? (
        <section className="panel panel--d1" style={{ marginTop: 22 }}>
          <div className="panel__head">
            <h3 className="panel__title">Fixture List</h3>
            <span className="panel__note">Loading</span>
          </div>
          <Skeleton rows={8} />
        </section>
      ) : null}

      {panel.status === "error" ? <FailBanner error={panel.error} /> : null}

      {panel.status === "ready" && panel.data === null ? (
        <div style={{ marginTop: 22 }}>
          <EmptyState
            title="No Match Day Selected"
            detail="The schedule carries no days to appoint against yet. Load the fixtures first."
          />
        </div>
      ) : null}

      {panel.status === "ready" && panel.data !== null ? (
        <AppointmentsPanel
          key={day}
          scope={{ day }}
          initialView={panel.data}
          officials={officials}
          stageLabel={stageLabel}
          competitions={competitions}
          scheduleView={schedulePanel.status === "ready" ? schedulePanel.data : null}
        />
      ) : null}
    </>
  );
}
